import { type ReactNode, useEffect, useSyncExternalStore } from 'react';
import { AlertTriangleIcon } from 'lucide-react';
import { Button } from '@worldcoo/ui/components/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@worldcoo/ui/components/dialog';
import { cn } from '@worldcoo/ui/lib/utils';

/**
 * The bar across the top of the NGO area, dark, or orange once it warns of a deadline. The layout reserves its height
 * through `data-ngo-banner`.
 */
export function NgoNoticeBar(props: { icon: ReactNode; children: ReactNode; actions: ReactNode; warns?: boolean }) {
	return (
		<div
			data-ngo-banner
			className={cn(
				'flex h-13 items-center gap-4 px-4',
				props.warns ? 'bg-[#FF8235] text-[#2D2D2D]' : 'bg-[#35433F] text-white',
			)}
		>
			<span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-white">{props.icon}</span>
			<p className="flex-1 truncate tracking-[-0.2px]">{props.children}</p>
			{props.actions}
		</div>
	);
}

let openNoticeDialogs = 0;
const noticeDialogListeners = new Set<() => void>();

function countOpenNoticeDialog(change: 1 | -1): void {
	openNoticeDialogs += change;
	for (const listener of noticeDialogListeners) listener();
}

function subscribeToNoticeDialogs(listener: () => void): () => void {
	noticeDialogListeners.add(listener);
	return () => noticeDialogListeners.delete(listener);
}

/** Whether a notice dialog is open now, so the "agreement signed" pop-up waits for it instead of opening over it. */
export function useNoticeDialogOpen(): boolean {
	return useSyncExternalStore(
		subscribeToNoticeDialogs,
		() => openNoticeDialogs > 0,
		() => false,
	);
}

/** The "important notice" dialog: one button puts it away for the visit, the other takes the NGO where it acts. */
export function NgoNoticeDialog(props: {
	open: boolean;
	onDismiss: () => void;
	title: string;
	description: ReactNode;
	dismissLabel: string;
	action: ReactNode;
}) {
	useEffect(() => {
		if (!props.open) return;
		countOpenNoticeDialog(1);
		return () => countOpenNoticeDialog(-1);
	}, [props.open]);

	return (
		<Dialog open={props.open} onOpenChange={(next) => !next && props.onDismiss()}>
			<DialogContent className="w-[537px] max-w-[calc(100%-2rem)] gap-0 rounded-xl bg-white p-0 shadow-[0px_16px_32px_rgba(77,33,0,0.08)] ring-0 sm:max-w-[537px]">
				<div className="flex flex-col items-start gap-4 p-10">
					<div className="flex size-20 items-center justify-center rounded-2xl border border-[#EED9C9] bg-[#FFF7ED]">
						<AlertTriangleIcon className="size-10 text-[#B08C71]" strokeWidth={1.5} />
					</div>
					<div className="flex w-full flex-col gap-2">
						<DialogTitle className="text-[32px] leading-[42px] font-medium text-[#35433F]">{props.title}</DialogTitle>
						<DialogDescription className="text-base leading-[21px] text-[#6F7674]">
							{props.description}
						</DialogDescription>
					</div>
				</div>
				<div className="flex items-start gap-[17px] border-t border-[#F0F0F0] px-6 pt-6 pb-4">
					<Button
						variant="outline"
						onClick={props.onDismiss}
						className="h-12 rounded-xl border-[#35433F] px-4 text-base leading-[21px] font-normal tracking-[-0.2px] text-[#35433F]"
					>
						{props.dismissLabel}
					</Button>
					{props.action}
				</div>
			</DialogContent>
		</Dialog>
	);
}

/** The dialog's main button, around the link that takes the NGO where it acts. */
export function NgoNoticeDialogAction(props: { onClick: () => void; children: ReactNode }) {
	return (
		<Button
			asChild
			onClick={props.onClick}
			className="h-12 rounded-xl bg-[#FF8235] px-4 text-base leading-[21px] font-medium tracking-[-0.2px] text-[#2D2D2D] hover:bg-[#FF8235]/90"
		>
			{props.children}
		</Button>
	);
}
